export const CLIENT_NAME_PLACEHOLDER = 'This application';

/**
 * `client.name` already carries an approved `client_name` alias — the API
 * substitutes it before Connect sees the response.
 *
 * Use this where the name starts a sentence or stands alone; mid-sentence,
 * use `getClientDisplayNameInSentence`.
 */
export function getClientDisplayName(client: any): string {
  if (!client) return '';

  const branding = client.settings?.connectBranding;
  if (!branding) {
    return client.name;
  }

  if (!branding.aliasEnabled) {
    return CLIENT_NAME_PLACEHOLDER;
  }

  return branding.displayName || client.name;
}

export function isClientNamePlaceholder(name: string | undefined): boolean {
  return (
    name?.toLocaleLowerCase() === CLIENT_NAME_PLACEHOLDER.toLocaleLowerCase()
  );
}

/**
 * Only the placeholder is lowercased. Real client names keep their own
 * capitalization — "Athena Advisory" must never become "athena advisory".
 */
export function getClientDisplayNameInSentence(client: any): string {
  const displayName = getClientDisplayName(client);
  return isClientNamePlaceholder(displayName)
    ? displayName.toLocaleLowerCase()
    : displayName;
}

export const MANUAL_CONTACT_FALLBACK_NAME = 'Payroll';

/**
 * Deliberately diverges from `getClientDisplayName`: this name is typed into a
 * payroll system's contact form, and "This application Admin" is not a name
 * anyone can enter. With branding off, prefer the session's `client_name`
 * alias and fall back to a neutral name rather than the raw application name.
 */
export function getManualContactName(client: any, clientName?: string): string {
  const displayName = getClientDisplayName(client);
  if (displayName && !isClientNamePlaceholder(displayName)) {
    return displayName;
  }

  return clientName || MANUAL_CONTACT_FALLBACK_NAME;
}
